import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import resourcesToBackend from 'i18next-resources-to-backend'
import { detectHost } from '@/auth'
// EVERY language (en included) loads through the resourcesToBackend dynamic
// import below — each is its own chunk instead of JSON riding in the entry
// bundle. Root (main.tsx) waits for `i18nReady` before its first render so
// users never see raw keys. Do NOT import './locales/en' or the root
// './locales' barrel here: that would statically link locale data back into
// the entry.

/**
 * Embedded (iframe or native WebView), the HOST owns the language, as it owns
 * the theme (ThemeProvider): its boot `?lng=` and then studio:context decide.
 * This origin's stored language is a leftover from some other session, so it
 * is neither read nor written while hosted. Reading it is how the prod freeze
 * of 12 Oct started: a stored "en" kept winning over the host's "ne".
 */
export const isLanguageHosted = () => detectHost() !== 'none'

// Helper: normalize language code to our supported set
export const normalize = (lng?: string) => {
  if (!lng) return 'en'
  const low = lng.toLowerCase()
  if (low.startsWith('ne')) return 'ne'
  if (low.startsWith('hi')) return 'hi'
  return 'en'
}

const hosted = isLanguageHosted()

/** Resolves once the first language has loaded; main.tsx renders after it. */
export const i18nReady = i18n
  .use(LanguageDetector)
  .use(
    resourcesToBackend((language: string) =>
      import(`./locales/${language}/index.ts`).then((m) => m.default),
    ),
  )
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    // No `lng`: setting one makes i18next skip the detector entirely, which
    // is why the host's boot `?lng=` never won.
    //
    // No Suspense either. With a lazily loaded language, every switch to one
    // not yet loaded suspended the first `useTranslation` above any boundary
    // — LanguageProvider — and React REMOUNTED the whole provider tree: the
    // provider re-asserted its stored language and HostContextBridge
    // re-announced studio:ready, which the host answered with its locale
    // again (the 12 Oct freeze). `changeLanguage` already loads the new
    // language before it switches, so without Suspense a switch is a plain
    // re-render in the old language until the new one is ready. The one load
    // that has nothing to show yet, the first, is gated by `i18nReady`.
    // Chosen over preloading en/ne/hi up front, which would ship two unused
    // language chunks on every load.
    react: { useSuspense: false },
    // i18next console.info()s an ad for Locize on every init unless this is
    // false. Embedded that is TWO copies per page load — the dashboard's
    // instance and ours — in a console a developer is reading to find a real
    // problem (staging, 2026-08-27). Not a diagnostic, so it goes.
    showSupportNotice: false,
    interpolation: { escapeValue: false },
    detection: {
      // The URL wins (?lng=ne, the host's boot hint), then — standalone only —
      // this origin's saved choice, then the browser. Hosted, the saved choice
      // is skipped and nothing is cached: see isLanguageHosted.
      order: hosted
        ? ['querystring', 'navigator']
        : ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lng',
      caches: hosted ? [] : ['localStorage'],
    },
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (lngs, _ns, key) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(
          `[i18n] Missing key "${key}" for language(s) ${Array.isArray(lngs) ? lngs.join(",") : lngs}.`,
        );
      }
    },
  })

// Keep <html lang> in sync for fonts, A11y, SEO
export function syncHtmlLang(current = i18n.language) {
  const normalized = normalize(current)
  let lang = 'en'
  if (normalized === 'ne') lang = 'ne-NP'
  else if (normalized === 'hi') lang = 'hi-IN'
  
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', lang)
  }
}

// update <html lang> whenever language changes
i18n.on('languageChanged', syncHtmlLang)

export default i18n
